import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useSelector } from 'react-redux'
import numeral from 'numeral'
import cn from 'classnames'

import Table from '../../../Invoices/Table'

import styles from './styles.module.scss'

import { getCurrency } from '../../../../helpers/currencies'

const columnHelper = createColumnHelper()

const formatNumber = (value, currency) => {
  return `${numeral(value).format('0,0.[00]')} ${currency}`
}

const ProductsConsumption = ({productsInStore, transactions}) => {

  const { t } = useTranslation()

  const companyInfo = useSelector(state => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)

  const data = useMemo(() => {
    const transactionsByProduct = transactions.filter(tr => tr['Amount.Out'] > 0).reduce((acc, transaction) => {
      return {
        ...acc,
        [transaction['Product.Id']]: acc[transaction['Product.Id']] ? [...acc[transaction['Product.Id']], transaction] : [transaction],
      }
    }, {})

    const productsSales = Object.entries(transactionsByProduct).reduce((acc, [productId, transactions]) => {
      return {
        ...acc,
        [productId]: {
          transactions: transactions,
          ...transactions.reduce((acc1, tr) => {
          return {
              ...acc1,
              measureUnit: tr['Product.MeasureUnit'] || '',
              totalSalesAmount: acc1.totalSalesAmount + (tr['Sum.Outgoing'] || 0),
              totalSalesQuantity: acc1.totalSalesQuantity + (tr['Amount.Out'] || 0),
            }
          }, {
            totalSalesAmount: 0,
            totalSalesQuantity: 0,
          })
        }
      }
    }, {})

    return productsInStore.filter(item => item.diff_amount > 0 && item.product).sort((a, b) => b.diff_amount - a.diff_amount).slice(0, 20).map((item) => {
      const diffInStore = numeral(item.diff_amount).format('0.[000]')
      const actualSalesQuantity = numeral(productsSales[item.product?.productId] ? productsSales[item.product.productId].totalSalesQuantity : 0).format('0.[000]')

      const diffPercentage = ((numeral(actualSalesQuantity).value() - numeral(diffInStore).value()) / numeral(diffInStore).value()) * 100
      const diffAmount = numeral(actualSalesQuantity).value() - numeral(diffInStore).value()

      return {
        name: item.product.name.replace('ž:', ''),
        category: item.product.category || 'Uncategorized',
        unit: item.product.measureUnit || productsSales[item.product.productId]?.measureUnit || '',
        diff_in_strore: diffInStore,
        actual_sales_quantity: actualSalesQuantity,
        diff_percentage: numeral(Number.isNaN(diffPercentage) ? 0 : diffPercentage).format('0.[00]') + '%',
        diff_amount_value: numeral(diffAmount).format('0.[000]'),
        avg_price: productsSales[item.product.productId] ? productsSales[item.product.productId].totalSalesAmount / productsSales[item.product.productId].totalSalesQuantity : 0,
        amount: productsSales[item.product.productId] ? productsSales[item.product.productId].totalSalesAmount : 0,
      }
    })
  }, [productsInStore, transactions])

  // console.log('transactions', transactions.filter(tr => tr['Amount.Out'] > 0))

  const columns = useMemo(() => ([
    columnHelper.accessor('name', {
      id: 'name',
      header: t('Product'),
      enableSorting: false,
      cell: ({ getValue, row }) => getValue()
    }),
    columnHelper.accessor('category', {
      id: 'category',
      header: t('Category'),
      enableSorting: false,
      cell: ({ getValue, row }) => getValue()
    }),
    columnHelper.accessor('unit', {
      id: 'unit',
      header: t('Unit'),
      enableSorting: false,
      cell: ({ getValue, row }) => getValue()
    }),
    columnHelper.accessor('diff_in_strore', {
      id: 'diff_in_strore',
      header: t('Written off'),
      enableSorting: false,
      cell: ({ getValue, row }) => getValue()
    }),
    columnHelper.accessor('actual_sales_quantity', {
      id: 'actual_sales_quantity',
      header: t('Sold'),
      enableSorting: false,
      cell: ({ getValue }) => getValue()
    }),
    columnHelper.accessor('avg_price', {
      id: 'avg_price',
      header: t('Average price'),
      enableSorting: false,
      cell: ({ getValue, row }) => formatNumber(getValue(), currency.symbol)
    }),
    columnHelper.accessor('amount', {
      id: 'amount',
      header: t('Amount'),
      enableSorting: false,
      cell: ({ getValue, row }) => formatNumber(getValue(), currency.symbol)
    }),
    columnHelper.accessor('diff_percentage', {
      id: 'diff_percentage',
      enableSorting: false,
      cell: ({ getValue }) => {
        return (
          <span className={cn({[styles.negative]: numeral(getValue()).value() < 0, [styles.positive]: numeral(getValue()).value() > 0})}>{ getValue() }</span>
        )
      },
      header: t('Difference %'),
    }),
    columnHelper.accessor('diff_amount_value', {
      id: 'diff_amount_value',
      enableSorting: false,
      cell: ({ getValue }) => {
        return (
          <span className={cn({[styles.negative]: numeral(getValue()).value() < 0, [styles.positive]: numeral(getValue()).value() > 0})}>{ getValue() }</span>
        )
      },
      header: t('Difference'),
    }),
    columnHelper.display({
      id: 'status',
      enableSorting: false,
      cell: ({ row }) => {
        const diffPercentage = numeral(row.original.diff_percentage).value()
        if (diffPercentage > 0) {
          return <span className={styles.statusPositive}>{t('Over')}</span>
        } else if (diffPercentage < 0) {
          return <span className={styles.statusNegative}>{t('Under')}</span>
        } else {
          return <span className={styles.statusNeutral}>{t('Equal')}</span>
        }
      },
      header: t('Status'),
    })
  ]), [t, currency.symbol])

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('TOP consumed')}</div>
      </div>

      <Table
        table={table}
        columns={columns}
        wrapperStyle={{flex: 1}}
        headerClassName={styles.tableHeader} />
    </div>
  )
}

export default ProductsConsumption
